import React, { useCallback, useMemo, useState } from "react";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { Button, Stack } from "@mui/material";
import { ProgressOverlay } from "components";
import { FilesPickerModal } from "components/modals/Files-picker/FilesPicker";
import { fileExtensions } from "constants/file";
import { useModals } from "hooks";
import { useDealInvestorsHook } from "hooks/deal-hooks/use-deal-invetors-hook";
import { IApiServiceError, useAccountSelector, useMeasureParametersSelector } from "store";
import { CurrencyType, IFile, MeasureDataDraft, MonitoringDataFiles, UiMeasureDataFiles } from "store/types";

import { currencySign as currencySignSet, ErrorMessage } from "../../../constants";
import { Footer } from "../Drawer/Footer";
import { RoundsManageModal } from "../Rounds-manage-modal/RoundsManageModal";
import { CommentsPopover } from "./CommentPopover";
import { FilesTable } from "./FilesTable";
import { Group as GroupComponent } from "./Group";
import {
  CommentPopup,
  getDefaultGroup,
  Group,
  GroupFormDealData,
  GroupsFormContext,
  IGroupsFormContext,
  MeasureData,
  MeasureDataErrors,
  MeasureDataFilesPickerParam,
  MeasureDataGroup,
  MeasureDataRound,
  RoundsByDealsErrors
} from "./GroupsFormContext";
import { useCalculateMeasureData } from "./hooks/use-calculate-measure-data";
import { useGroup } from "./hooks/use-group";
import { useMeasureData } from "./hooks/use-measure-data";
import { ImportCommitments } from "./ImportCommitments";

const currencyError = "Submit with different currency was declined";

interface Props extends GroupFormDealData {
  onSave: (data: {
    groups: MeasureDataGroup[];
    roundsByDeals: Record<number, MeasureDataRound[]>;
  }) => void | Promise<void>;
  onSaveAsDraft: (data: MeasureDataDraft["data"]) => Promise<void>;
  onClose: () => void;
  onSkip?: () => void;
  initialGroups?: Group[];
  initialMeasureData?: MeasureData[];
  initialRoundsByDeal?: Record<number, MeasureDataRound[]>;

  batch_id?: number;

  disableDealSelect?: boolean;
  disableSubDealSelect?: boolean;
  disableAddGroup?: boolean;
  disableRemoveGroup?: boolean;
  disableAddMeasureData?: boolean;
}

export interface IHandleDeleteFileFromMeasureData {
  key: number | null;
  group_id: number;
  localId: string;
  roundId: number | string | null;
}

export const GroupsForm: React.FC<Props> = ({
  deal_id,
  defaultCurrency,
  onSave,
  onSaveAsDraft,
  onClose,
  onSkip,
  initialGroups,
  initialMeasureData = [],
  disableAddGroup,
  batch_id,
  initialRoundsByDeal = {},
  ...disabledOptions
}) => {
  const measureParameters = useMeasureParametersSelector();
  const { batch_runtime_summary } = useAccountSelector() || {};
  const { dealInvestors } = useDealInvestorsHook(deal_id);

  const [groups, setGroups] = useState<Group[]>(initialGroups || [getDefaultGroup({ id: 1, ...{ deal_id } })]);
  // Enrich initialMeasureData with investor_group from investors list
  const [measureData, setMeasureData] = useState<MeasureData[]>(initialMeasureData);

  const [rounds, setRounds] = useState<Record<number, MeasureDataRound[]>>(initialRoundsByDeal); // { [deal_id]: { ...round } };
  const [loadingGroupId, setLoadingGroupId] = useState<number | null>(null);
  const [commentPopup, setCommentPopup] = useState<CommentPopup | null>(null);
  const [measureDataErrors, setMeasureDataErrors] = useState<Record<MeasureData["key"], MeasureDataErrors>>();
  const [roundsByDealsErrors, setRoundsByDealsErrors] = useState<RoundsByDealsErrors | null>(null);
  const [filesErrors, setFilesErrors] = useState<string[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [manageDealRounds, setManageDealRounds] = useState<number>(0);
  const [importCommitmentsModal, setImportCommitmentsModal] = useState<Pick<Group, "deal_id" | "sub_deal_id"> | null>(
    null
  );
  const [measureDataFilesPickerParam, setMeasureDataFilesPickerParam] = useState<MeasureDataFilesPickerParam | null>(
    null
  );

  const { errorModal, warningModal } = useModals();

  const totalValue: {
    value: number;
    totalValueString: string;
  } = useMemo(() => {
    const valuableMeasureData = measureData
      .filter((item) => item.value)
      .map((item) => {
        const groupMeasureId = groups.find((group) => group.id === item.group_id)?.measure_id;

        if (!groupMeasureId) return item;

        const isNegativeMeasure = measureParameters.find((measure) => measure.id === groupMeasureId)?.is_negative;

        if (isNegativeMeasure) {
          return {
            ...item,
            value: String(Number(item.value) * -1)
          };
        }

        return item;
      });

    const currency = valuableMeasureData.reduce(
      (currency: CurrencyType | null, measureData, _, arr) => {
        if (!measureData.currency) return currency;

        if (!currency) return measureData.currency;

        if (currency !== measureData.currency) {
          arr.slice(1);
          return null;
        }

        return currency;
      },
      null as CurrencyType | null
    );

    const currencySign = currency ? currencySignSet[currency] : "";

    const totalValue = valuableMeasureData.length
      ? valuableMeasureData.reduce((acc, item) => (acc += Number(item.value)), 0)
      : NaN;
    return {
      totalValueString: isNaN(totalValue) ? "" : totalValue.toLocaleString() + currencySign,
      value: totalValue
    };
  }, [measureData, measureParameters, groups]);

  const getMaxMeasureDataKey = useCallback((): number => {
    return measureData.reduce((acc, item) => (item.key > acc ? item.key : acc), 0);
  }, [measureData]);

  const { createMeasureData, updateMeasureData, deleteMeasureData, updateManyMeasureData, updateGroupMeasureData } =
    useMeasureData({
      getMaxMeasureDataKey,
      setMeasureData,
      setRounds,
      setMeasureDataErrors,

      measureData,
      groups,
      rounds,

      batch_id,
      defaultCurrency
    });

  const getDealRoundsFromState = useCallback<IGroupsFormContext["getDealRoundsFromState"]>(
    ({ dealId, filterDeletedRounds }) => {
      let roundsFromCache = rounds?.[dealId];

      if (!roundsFromCache) return null;

      if (filterDeletedRounds) {
        roundsFromCache = roundsFromCache.filter((item) => !item.is_deleted);
      }

      return roundsFromCache;
    },
    [rounds, measureData]
  );

  const { createGroup, updateGroup, deleteGroup, importCommitments } = useGroup({
    setGroups,
    getMaxMeasureDataKey,
    setLoadingGroupId,
    getDealRoundsFromState,
    setRounds,
    setMeasureData,
    setMeasureDataErrors,

    groups,
    deal_id,
    measureParameters,
    defaultCurrency,
    batch_id,
    measureData
  });

  const calculateMeasureData = useCalculateMeasureData({ measureData, getMaxMeasureDataKey, setMeasureData });

  const selectFromMeasureData = useCallback<IGroupsFormContext["selectFromMeasureData"]>(
    (where) => {
      return measureData.filter((item) => item.group_id === where.group_id);
    },
    [measureData]
  );

  const getMeasureDataErrors = useCallback<IGroupsFormContext["getMeasureDataErrors"]>(
    (key) => {
      return measureDataErrors?.[key];
    },
    [measureDataErrors]
  );

  const findFirstMeasureDataError = useCallback<IGroupsFormContext["findFirstMeasureDataError"]>(
    (keys) => {
      if (!measureDataErrors) return;

      const errorMeasureDataId = keys.find((key) => {
        const errors = measureDataErrors[key];
        if (!errors) return;

        if (errors.accounting_date || errors.date) {
          return key;
        }
      });

      if (!errorMeasureDataId) return;

      return measureDataErrors[errorMeasureDataId];
    },
    [measureDataErrors]
  );

  const clearRoundsErrors = useCallback(
    (dealId: number) => {
      if (!roundsByDealsErrors) return;

      if (!roundsByDealsErrors[dealId]) return;
      setRoundsByDealsErrors((prevState) => {
        const errorsCopy = { ...prevState };

        delete errorsCopy[dealId];

        if (Object.entries(errorsCopy).length) {
          return errorsCopy;
        } else {
          return null;
        }
      });
    },
    [roundsByDealsErrors]
  );

  const getDealRoundsErrors = useCallback<IGroupsFormContext["getDealRoundsErrors"]>(
    (dealId) => {
      if (!roundsByDealsErrors) return null;

      return roundsByDealsErrors[dealId] || null;
    },
    [roundsByDealsErrors]
  );

  const checkDifferentCurrency = useCallback(
    (measureData: MeasureData[]) =>
      new Promise((resolve, reject) => {
        if (
          measureData.some(({ currency }) => {
            if (!currency) return;
            return currency !== defaultCurrency;
          })
        ) {
          return warningModal({
            action: async () => resolve(true),
            cancelAction: async () => reject(currencyError),
            msg: "You are about to enter values in a different currency that is defined on the deal. Are you sure?",
            onPressSuccess: "Continue"
          });
        }
        resolve(true);
      }),
    [measureData, defaultCurrency, warningModal]
  );

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      setFilesErrors(null);

      const filteredMeasureData = measureData.filter((item) => {
        if (!item.value) return false; // Remove elements without value
        // if (!item.key) return false; // Remove zero elements - zero element just to display empty round
        if (item.round_id && !item.investor_id) return false; // Remove elements without investors (newly created)
        return true;
      });

      const measureDataGrouped: MeasureDataGroup[] = groups
        .map((group) => ({
          ...group,
          data: filteredMeasureData.filter((item) => item.group_id === group.id)
        }))
        .filter((group) => group.data.length);

      const filteredRounds: Record<number, MeasureDataRound[]> = Object.entries(rounds || {}).reduce(
        (acc, [dealId, rounds]) => {
          return {
            ...acc,
            [dealId]: rounds.filter((item) => {
              if (typeof item.id === "string" && item.is_deleted) return false;
              return true;
            })
          };
        },
        {}
      );

      await checkDifferentCurrency(filteredMeasureData);

      await onSave({ groups: measureDataGrouped, roundsByDeals: filteredRounds });
    } catch (error) {
      if (error === currencyError) return;
      // setMeasureDataErrors(error as Record<MeasureData["id"], MeasureDataErrors>);
      const apiError = error as IApiServiceError;
      setMeasureDataErrors(apiError.errors as Record<MeasureData["key"], MeasureDataErrors>);

      const apiErrors = (apiError.errors || {}) as Record<string, unknown>;
      const fileErrorValue = apiErrors.file;
      if (Array.isArray(fileErrorValue) && fileErrorValue.length) {
        setFilesErrors(Array.from(new Set(fileErrorValue as string[])));
      }

      if (apiError.response?.roundsByDealsErrors) {
        setRoundsByDealsErrors(apiError.response.roundsByDealsErrors);
      }
      if (apiError.message !== ErrorMessage.NoMessage) {
        errorModal({ msg: apiError.message });
      }
    } finally {
      setLoading(false);
    }
  }, [groups, measureData, onSave, setMeasureDataErrors, checkDifferentCurrency, rounds]);

  const handleSubmitRounds = useCallback(
    ({ dealId, state }: { dealId: number; state: MeasureDataRound[] }) => {
      setRounds((prevState) => ({ ...prevState, [dealId]: state }));
      clearRoundsErrors(dealId);
    },
    [setRounds]
  );

  const clearFilesErrors = useCallback(() => {
    setFilesErrors(null);
    setMeasureDataErrors((prev) => {
      if (!prev || !("file" in prev)) return prev;
      const next = { ...prev } as Record<string, MeasureDataErrors>;
      delete next.file;
      return Object.keys(next).length ? (next as Record<MeasureData["key"], MeasureDataErrors>) : undefined;
    });
  }, []);

  const measureDataFilesPickerSubmit = async ({
    data,
    key,
    groupId,
    isTotal,
    roundId
  }: {
    data: { filesIds: number[]; attachments: File[]; files: IFile[] };
    key: number | null;
    groupId: number | null;
    isTotal: boolean;
    roundId: string | number | null;
  }) => {
    clearFilesErrors();
    // create payload only if there are files
    let filesPayload: UiMeasureDataFiles | null = null;

    if (data.attachments.length || data.files.length) {
      filesPayload = {
        localFiles: null,
        dbFiles: null
      };

      if (data.attachments.length) {
        filesPayload.localFiles = data.attachments.reduce(
          (acc, file) => {
            acc[crypto.randomUUID()] = file;
            return acc;
          },
          {} as Record<string, File>
        );
      }

      if (data.files.length) {
        filesPayload.dbFiles = data.files.reduce(
          (acc, file) => {
            acc[crypto.randomUUID()] = {
              name: file.name,
              id: file.id,
              file_url: file.file_url || ""
            };
            return acc;
          },
          {} as Record<string, MonitoringDataFiles>
        );
      }
    }

    // update  measure data
    if (!isTotal && key) {
      setMeasureData((prevState) =>
        prevState.map((item) => {
          if (item.key === key) {
            return { ...item, files: filesPayload };
          }
          return item;
        })
      );
    }
    // update group
    else if (isTotal && groupId && roundId) {
      setGroups((prevState) =>
        prevState.map((item) => {
          if (item.id !== groupId) return item;

          if (filesPayload) {
            return {
              ...item,
              files: { ...(item.files || {}), [roundId]: filesPayload }
            };
          }

          return item;
        })
      );
    }

    setMeasureDataFilesPickerParam(null);
  };

  const updateGroupDescription = useCallback<IGroupsFormContext["updateGroupDescription"]>(
    ({ groupId, roundId, description }) => {
      setGroups((prevState) =>
        prevState.map((group) => {
          if (group.id !== groupId) return group;

          const trimmed = description.trim();
          const currentDescriptions = group.descriptions || {};
          const nextDescriptions = { ...currentDescriptions };

          if (trimmed) {
            nextDescriptions[roundId] = trimmed;
          } else {
            delete nextDescriptions[roundId];
          }

          return {
            ...group,
            descriptions: Object.keys(nextDescriptions).length ? nextDescriptions : null
          };
        })
      );
    },
    []
  );

  const handleDeleteFileFromMeasureData = ({ key, group_id, localId, roundId }: IHandleDeleteFileFromMeasureData) => {
    clearFilesErrors();
    const removeFileFromFiles = (files: UiMeasureDataFiles | null): UiMeasureDataFiles | null => {
      if (!files) return null;

      const remainingLocalFiles = files.localFiles ? { ...files.localFiles } : null;
      const remainingDbFiles = files.dbFiles ? { ...files.dbFiles } : null;

      if (remainingLocalFiles && localId in remainingLocalFiles) {
        delete remainingLocalFiles[localId];
      }

      if (remainingDbFiles && localId in remainingDbFiles) {
        delete remainingDbFiles[localId];
      }

      const hasLocalFiles = remainingLocalFiles && Object.keys(remainingLocalFiles).length > 0;
      const hasDbFiles = remainingDbFiles && Object.keys(remainingDbFiles).length > 0;

      if (!hasLocalFiles && !hasDbFiles) {
        return null;
      }

      return {
        localFiles: hasLocalFiles ? remainingLocalFiles : null,
        dbFiles: hasDbFiles ? remainingDbFiles : null
      };
    };

    // delete file from measure data
    if (key) {
      setMeasureData((prevState) =>
        prevState.map((item) => (item.key === key ? { ...item, files: removeFileFromFiles(item.files) } : item))
      );
      return;
    }

    // delete file from group (total)
    if (group_id && roundId) {
      setGroups((prevState) =>
        prevState.map((group) => {
          if (group.id !== group_id) return group;

          if (!group.files) return group;

          const updatedFilesByRound: Record<string | number, UiMeasureDataFiles> = {};
          let hasAnyFiles = false;

          Object.entries(group.files).forEach(([currentRoundId, files]) => {
            const updatedFiles = removeFileFromFiles(files);
            if (updatedFiles) {
              updatedFilesByRound[currentRoundId] = updatedFiles;
              hasAnyFiles = true;
            }
          });

          return {
            ...group,
            files: hasAnyFiles ? updatedFilesByRound : null
          };
        })
      );
    }
  };

  return (
    <ProgressOverlay inProgress={loading} containerSX={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
      <Stack flexGrow={1} sx={{ overflow: "hidden" }}>
        <GroupsFormContext.Provider
          value={{
            createGroup,
            updateGroup,
            deleteGroup,
            selectFromMeasureData,
            updateMeasureData,
            updateManyMeasureData,
            calculateMeasureData,
            setCommentPopup,
            getMeasureDataErrors,
            findFirstMeasureDataError,
            createMeasureData,
            deleteMeasureData,
            getDealRoundsFromState,
            manageRounds: setManageDealRounds,
            getDealRoundsErrors,
            setImportCommitmentsModal,
            importCommitments,
            setMeasureDataFilesPickerParam,
            updateGroupMeasureData,
            updateGroupDescription,
            dealInvestors
          }}
        >
          <Stack sx={{ flexGrow: 1, overflow: "auto" }}>
            <Stack
              sx={{
                ...(groups.length && { p: "20px 0" }),
                gap: "20px",
                ".group": {
                  borderBottom: "1px solid #DAE0E2",
                  pb: "20px",
                  ":last-child": { pb: 0, borderBottom: "none" }
                }
              }}
            >
              {groups.map((item) => {
                const isExistingGroup = Boolean(initialGroups?.find((group) => group.id === item.id));
                return (
                  <ProgressOverlay key={item.id} inProgress={item.id === loadingGroupId} disableOverlayBgc>
                    <GroupComponent
                      defaultCurrency={defaultCurrency}
                      enableDealSelect={!deal_id}
                      measureParameters={measureParameters}
                      {...item}
                      {...disabledOptions}
                      disableDealSelect={isExistingGroup}
                      disableSubDealSelect={isExistingGroup}
                    />
                  </ProgressOverlay>
                );
              })}
            </Stack>
            {!disableAddGroup && (
              <Stack
                sx={{
                  alignItems: "flex-start",
                  p: "20px 0",
                  m: "0 20px",
                  borderTop: "1px solid #DBE8F5",
                  ...(batch_runtime_summary && { paddingBottom: "40px" })
                }}
              >
                <Button onClick={createGroup} size="sm" variant="outlined" startIcon={<AddOutlinedIcon />}>
                  Add Parameter
                </Button>
              </Stack>
            )}

            <FilesTable
              measureData={measureData}
              groups={groups}
              handleDeleteFileFromMeasureData={handleDeleteFileFromMeasureData}
              filesErrors={filesErrors}
            />
          </Stack>
          {commentPopup && <CommentsPopover {...commentPopup} />}
          <RoundsManageModal
            dealId={manageDealRounds}
            onClose={() => setManageDealRounds(0)}
            onSubmit={handleSubmitRounds}
            rounds={rounds}
          />
          <ImportCommitments deals={importCommitmentsModal} onClose={() => setImportCommitmentsModal(null)} />

          {(measureDataFilesPickerParam?.key || measureDataFilesPickerParam?.groupId) && (
            <FilesPickerModal
              filesPickerModalOpen={Boolean(measureDataFilesPickerParam?.key || measureDataFilesPickerParam?.groupId)}
              onClose={() => setMeasureDataFilesPickerParam(null)}
              currentFiles={
                measureDataFilesPickerParam.files?.dbFiles
                  ? Object.values(measureDataFilesPickerParam.files.dbFiles).map((file) => ({
                      id: file.id,
                      upload_type: "section",
                      name: file.name,
                      file_url: file.file_url
                    }))
                  : []
              }
              attachedFiles={
                measureDataFilesPickerParam.files?.localFiles
                  ? Object.values(measureDataFilesPickerParam.files.localFiles)
                  : []
              }
              filesPickerSubmit={(data) =>
                measureDataFilesPickerSubmit({
                  data: {
                    filesIds: data.filesIds || [],
                    attachments: data.attachments || [],
                    files: data.files || []
                  },
                  key: measureDataFilesPickerParam.key,
                  groupId: measureDataFilesPickerParam.groupId,
                  isTotal: measureDataFilesPickerParam.isTotal,
                  roundId: measureDataFilesPickerParam.roundId
                })
              }
              title="Add files"
              workspaceTitle="Select or upload files"
              dealId={deal_id}
              getFilesOutsideDeal={true}
              fileUploadType={["section"]}
              selectWithoutFiles={true}
              acceptedFilesExtensions={fileExtensions.map((extension) => `.${extension}`)}
            />
          )}
        </GroupsFormContext.Provider>
        <Footer
          totalValue={totalValue.totalValueString}
          onSave={handleSubmit}
          onSkip={onSkip}
          onSaveAsDraft={() => onSaveAsDraft({ group: groups, measure_data: measureData, rounds })}
          disableSubmit={
            Boolean(measureDataErrors && Object.entries(measureDataErrors).length) ||
            isNaN(totalValue.value) ||
            Boolean(roundsByDealsErrors)
          }
          onCancel={onClose}
          batchId={batch_id}
          measureData={measureData}
          groups={groups}
          dealId={deal_id!}
          roundsByDeals={rounds}
        />
      </Stack>
    </ProgressOverlay>
  );
};
